import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { FileText, LogOut, ChevronLeft, ChevronRight, Trash2, Save, X, CheckSquare, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

import { BACKEND_URL, API_BASE as API } from '../config';

const ALL_DOCS = [
  { label: 'Pictures Of Vehicle / Property Damage(s)', field: 'damage_pics' },
  { label: 'DL or CDL, Insurance, Registration of Other Vehicle', field: 'other_vehicle_docs' },
  { label: 'DOT#, Truck#, Plate#, Trailer# of Other CMV', field: 'other_cmv' },
  { label: 'Police Report # or Exchange Form', field: 'pr_or_ef' },
  { label: 'Company Truck Reg., Lease Agreement, Rate Con., BOL', field: 'our_truck_docs' },
  { label: 'Video Footage', field: 'video_footage' },
  { label: 'Drivers Written Statement', field: 'drivers_ws' },
  { label: 'Damage Repair Estimate(s)', field: 'estimates' },
  { label: 'Other Supporting Docs', field: 'other_docs' },
  { label: '"Log Books"', field: 'log_books' },
  { label: 'Drug & Alcohol Test Results', field: 'da_test' },
];

function isImageFile(name) {
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name || '');
}

const DocsModal = ({ open, onClose, claim, initialFiles = null, onUploadSuccess = null }) => {
  const { user } = useAuth();
  const canUpload = user?.role && user.role !== 'viewer';
  const [pictures, setPictures] = useState([]);
  const [initialPicCount, setInitialPicCount] = useState(0);
  const [picIndex, setPicIndex] = useState(0);
  const [showUploaded, setShowUploaded] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [checkboxes, setCheckboxes] = useState({});
  const [initialCheckboxes, setInitialCheckboxes] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSaveWarning, setShowSaveWarning] = useState(false);
  const [saveWarningMsg, setSaveWarningMsg] = useState('');
  const [uploadedThisSession, setUploadedThisSession] = useState(false);
  // Track whether we've already auto-uploaded a passed-in initialFiles set so
  // re-renders don't re-upload the same blob. Bumped to a new sentinel each
  // time the caller passes a fresh batch of files.
  const lastInitialBatchRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (claim && open) {
      setPicIndex(0);
      axios.get(`${API}/claim-docs/files/${encodeURIComponent(claim.report_no)}`, { withCredentials: true })
        .then(async (res) => {
          const existing = res.data || [];
          setPictures(existing);
          setInitialPicCount(existing.length);
          // Auto-upload any files supplied via `initialFiles` (e.g. the
          // email viewer's "Save in claim" hand-off). We do this here —
          // SEQUENCED AFTER the existing-files GET resolves — so the
          // pre-upload baseline is locked in before pictures change. If we
          // ran upload in a parallel useEffect, the GET response could land
          // after the upload and clobber `initialPicCount` to the post-
          // upload count, leaving `filesChanged = false` and the Save bar
          // hidden (the bug user reported).
          //
          // We also deduplicate: any incoming file whose (name, size) matches
          // an existing claim file is silently skipped — prevents the email
          // viewer from creating duplicate copies when the user accidentally
          // hits "Save in claim" twice on the same attachment.
          if (initialFiles && initialFiles.length && lastInitialBatchRef.current !== initialFiles) {
            lastInitialBatchRef.current = initialFiles;
            const existingFingerprints = new Set(
              existing.map((p) => `${p.name}|${p.size ?? ''}`)
            );
            const fresh = [];
            const dupes = [];
            for (const f of initialFiles) {
              const fp = `${f.name}|${f.size ?? ''}`;
              if (existingFingerprints.has(fp)) {
                dupes.push(f.name);
              } else {
                fresh.push(f);
              }
            }
            if (dupes.length) {
              toast.warning(
                dupes.length === 1
                  ? `"${dupes[0]}" is already saved to this claim`
                  : `${dupes.length} files already saved — skipped`
              );
            }
            if (fresh.length) {
              await handleUpload(fresh);
            } else {
              // All were duplicates — still notify the email viewer so the
              // chip checkmark / badge updates without forcing another save.
              onUploadSuccess?.(initialFiles);
            }
          }
        })
        .catch(() => { setPictures([]); setInitialPicCount(0); });
      const cb = {};
      ALL_DOCS.forEach(d => { cb[d.field] = claim[d.field] === true || claim[d.field] === 'true'; });
      setCheckboxes(cb);
      setInitialCheckboxes({ ...cb });
      setUploadedThisSession(false);
    }
  }, [claim, open, initialFiles]);

  const uploaded = ALL_DOCS.filter(d => checkboxes[d.field]);
  const missing = ALL_DOCS.filter(d => !checkboxes[d.field]);
  const currentPic = pictures[picIndex] || null;

  // Dirty = checkboxes changed OR files added/removed
  const cbChanged = JSON.stringify(checkboxes) !== JSON.stringify(initialCheckboxes);
  const hasNewChecks = ALL_DOCS.some(d => checkboxes[d.field] && !initialCheckboxes[d.field]);
  const filesChanged = pictures.length !== initialPicCount;
  const filesAdded = pictures.length > initialPicCount;
  const isDirty = cbChanged || filesChanged;

  const handleToggle = (field) => {
    setCheckboxes(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const doSave = async () => {
    setSaving(true);
    setShowSaveWarning(false);
    try {
      // 1) The actual save — only this affects success/failure messaging
      await axios.put(`${API}/claim-docs/checkboxes`, { report_no: claim.report_no, ...checkboxes }, { withCredentials: true });
      toast.success('Changes saved');
      setInitialCheckboxes({ ...checkboxes });
      setUploadedThisSession(false);
    } catch (e) {
      // Special-case 404 right after a successful upload: the file IS persisted
      // on the claim (the upload endpoint succeeded), but the claim was
      // renamed/deleted before this checkbox save fired. Show a clearer
      // message instead of the alarming "Claim not found" backend detail.
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 404 && uploadedThisSession) {
        toast.warning('Files were saved, but checkbox state could not be updated (claim was modified). Please refresh and try again.');
      } else {
        toast.error(detail || 'Failed to save');
      }
      setSaving(false);
      return;
    }

    // 2) Best-effort refresh of the file list. If this fails (transient network, etc.)
    //    the save still succeeded — do NOT show a "Failed to save" toast.
    try {
      const res = await axios.get(`${API}/claim-docs/files/${encodeURIComponent(claim.report_no)}`, { withCredentials: true });
      setPictures(res.data || []);
      setInitialPicCount((res.data || []).length);
      setPicIndex(0);
    } catch (e) {
      // Silent — the save succeeded; user can reopen modal to see latest files.
      console.warn('Post-save file refresh failed:', e?.response?.status, e?.response?.data);
    }

    window.dispatchEvent(new CustomEvent('claim-docs-updated'));
    setSaving(false);
  };

  const handleSave = () => {
    if (hasNewChecks && !filesAdded && !uploadedThisSession) {
      setSaveWarningMsg('Saving Selection Without Document');
      setShowSaveWarning(true);
      return;
    }
    if (uploadedThisSession && !hasNewChecks) {
      setSaveWarningMsg('Saving Document Without Selection');
      setShowSaveWarning(true);
      return;
    }
    doSave();
  };

  const handleCancel = () => {
    setCheckboxes({ ...initialCheckboxes });
    setUploadedThisSession(false);
    // Re-fetch files from server to revert any uploads/deletes made during this session
    axios.get(`${API}/claim-docs/files/${encodeURIComponent(claim.report_no)}`, { withCredentials: true })
      .then(res => { setPictures(res.data || []); setInitialPicCount((res.data || []).length); setPicIndex(0); })
      .catch(() => {});
  };

  const handleUpload = async (files) => {
    if (!files?.length || !claim?.report_no) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('report_no', claim.report_no);
      for (const f of files) fd.append('files', f);
      const res = await axios.post(`${API}/claim-docs/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPictures(res.data.uploaded || []);
      setPicIndex(Math.max(0, (res.data.uploaded || []).length - files.length));
      setUploadedThisSession(true);
      toast.success(`${files.length} file(s) uploaded`);
      // Notify the opener (e.g. EmailThreadModal) that these files are now
      // persisted on the claim. The opener uses this to mark the source
      // email's attachment indexes as "saved to claim" so the badge shows.
      onUploadSuccess?.(files);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleFileInput = (e) => handleUpload(e.target.files);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleDeletePic = async (key) => {
    try {
      await axios.delete(`${API}/claim-docs/files?report_no=${encodeURIComponent(claim.report_no)}&file_key=${encodeURIComponent(key)}`, { withCredentials: true });
      const updated = pictures.filter(p => p.key !== key);
      setPictures(updated);
      setPicIndex(Math.min(picIndex, Math.max(0, updated.length - 1)));
      toast.success('File deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        /* z-[70] sits above the parent EmailThreadModal (z-[58]) so the
         * Radix overlay doesn't get visually buried + clicks reliably land
         * on DocsModal content rather than passing through to the
         * EmailThreadModal backdrop underneath. The matching guard on
         * EmailThreadModal's onClick (e.target === e.currentTarget) is the
         * primary close-protection — this z-index is belt-and-suspenders. */
        className="max-w-4xl w-full p-0 gap-0 overflow-hidden [&>button]:hidden z-[70]"
        data-testid="docs-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 pt-4 md:pt-5 pb-3">
          <div>
            <h2 className="text-lg md:text-2xl font-bold text-blue-600" data-testid="docs-modal-title">Add / Edit Document(s)</h2>
            {claim?.dot_recordable && <p className="text-red-600 font-bold text-sm" data-testid="docs-dot-recordable">DOT Recordable</p>}
          </div>
          {!isDirty && (
            <button onClick={() => onClose(false)} className="flex items-center gap-1.5 text-gray-700 hover:text-gray-900 font-medium text-sm mr-2 md:mr-6" data-testid="docs-modal-exit">
              <LogOut className="w-4 h-4" /> Exit
            </button>
          )}
        </div>

        {/* Save / Cancel bar — only when dirty */}
        {isDirty && (
          <div className="flex items-center justify-end gap-4 px-4 md:px-6 pb-3 border-b border-gray-200" data-testid="docs-save-bar">
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 text-gray-800 hover:text-blue-600 font-semibold text-sm" data-testid="docs-save-btn">
              <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={handleCancel} className="flex items-center gap-1.5 text-gray-800 hover:text-red-600 font-semibold text-sm" data-testid="docs-cancel-btn">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4 md:px-6 pb-6 max-h-[75vh] overflow-y-auto">
          {/* Left Column: Upload + Preview */}
          <div>
            <p className="text-gray-500 text-sm mb-2">Documents</p>
            {canUpload ? (
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4 ${dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-white'}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="docs-drop-zone"
              >
                <div className="flex justify-center gap-3 mb-3 text-gray-300">
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="1.5"/><circle cx="8.5" cy="8.5" r="1.5" strokeWidth="1.5"/><path d="m21 15-5-5L5 21" strokeWidth="1.5"/></svg>
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="1.5"/><path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" strokeWidth="1.5"/></svg>
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" strokeWidth="1.5"/></svg>
                </div>
                <p className="text-gray-500 text-sm mb-1">{uploading ? 'Uploading...' : 'Drag and drop your file'}</p>
                <p className="text-blue-500 underline text-sm mb-1">Click to select a file</p>
                <p className="text-gray-400 text-xs">or drop it here</p>
                <input type="file" ref={fileInputRef} onChange={handleFileInput} multiple className="hidden" data-testid="docs-file-input" />
              </div>
            ) : (
              <div className="border-2 border-dashed rounded-lg p-6 text-center mb-4 border-gray-200 bg-gray-50" data-testid="docs-upload-restricted">
                <p className="text-gray-400 text-sm">Document upload is not available for your role</p>
              </div>
            )}

            {/* Picture preview frame */}
            <div className="border border-gray-300 rounded p-3 bg-white" data-testid="docs-pic-preview">
              {pictures.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <a href={`${BACKEND_URL}${currentPic.url}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline text-sm truncate flex-1">{currentPic.name}</a>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs whitespace-nowrap">{currentPic.size ? `${(currentPic.size / 1024).toFixed(1)} KB` : ''}</span>
                      {canUpload && <button onClick={() => handleDeletePic(currentPic.key)} className="text-gray-400 hover:text-red-500" data-testid="docs-pic-delete"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded flex items-center justify-center min-h-[180px] relative mb-2">
                    {picIndex > 0 && <button onClick={() => setPicIndex(picIndex - 1)} className="absolute left-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 z-10" data-testid="docs-prev-btn"><ChevronLeft className="w-6 h-6" /></button>}
                    {isImageFile(currentPic.name) ? (
                      <img
                        src={`${BACKEND_URL}${currentPic.url}`}
                        alt={currentPic.name}
                        className="max-h-[180px] max-w-full object-contain"
                        onError={(e) => {
                          if (currentPic.original_url && !e.target.dataset.triedOriginal) {
                            e.target.dataset.triedOriginal = 'true';
                            e.target.src = currentPic.original_url;
                          }
                        }}
                      />
                    ) : (
                      <div className="text-center p-4"><FileText className="w-10 h-10 text-gray-400 mx-auto mb-2" /><p className="text-sm text-gray-500">{currentPic.name}</p></div>
                    )}
                    {picIndex < pictures.length - 1 && <button onClick={() => setPicIndex(picIndex + 1)} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 z-10" data-testid="docs-next-btn"><ChevronRight className="w-6 h-6" /></button>}
                  </div>
                  <p className="text-gray-500 text-xs">File {picIndex + 1} of {pictures.length}</p>
                </>
              ) : (
                <div className="flex items-center justify-center h-[120px] bg-gray-50 rounded">
                  <p className="text-sm text-gray-400 italic">No files uploaded</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column (stacks below preview on mobile) */}
          <div>
            {/* Missing Docs */}
            <p className="text-orange-500 font-bold text-lg mb-2" data-testid="docs-missing-title">Missing Docs</p>
            {hasNewChecks && !filesAdded && !uploadedThisSession && (
              <div className="flex items-center gap-2 mb-2" data-testid="docs-upload-selected-header">
                <Upload className="w-5 h-5 text-blue-600" />
                <span className="font-bold text-blue-600 text-sm">Upload Selected Document(s)</span>
              </div>
            )}
            {uploadedThisSession && !hasNewChecks && (
              <div className="flex items-center gap-2 mb-2" data-testid="docs-select-added-header">
                <CheckSquare className="w-5 h-5 text-blue-600" />
                <span className="font-bold text-blue-600 text-sm">Select Added Document(s)</span>
              </div>
            )}
            <div className="border border-gray-300 rounded p-4 bg-white space-y-4" data-testid="docs-missing-list">
              {missing.length > 0 ? missing.map(doc => (
                <div key={doc.field} className="flex items-start space-x-3">
                  <Checkbox id={`missing-${doc.field}`} checked={false} onCheckedChange={() => handleToggle(doc.field)} className="w-5 h-5 border-2 mt-0.5" data-testid={`missing-check-${doc.field}`} />
                  <label htmlFor={`missing-${doc.field}`} className="text-sm text-gray-700 leading-snug cursor-pointer">{doc.label}</label>
                </div>
              )) : (
                <p className="text-sm text-gray-400 text-center">All documents uploaded</p>
              )}
            </div>

            {/* Uploaded Docs */}
            <div className="flex items-center gap-2 mt-4" data-testid="docs-uploaded-toggle">
              <span className="text-blue-600 font-bold text-base underline">Uploaded Docs</span>
              <button onClick={() => setShowUploaded(true)} className={`text-sm ${showUploaded ? 'text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`} data-testid="docs-show-uploaded">Show</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => setShowUploaded(false)} className={`text-sm ${!showUploaded ? 'text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`} data-testid="docs-hide-uploaded">Hide</button>
            </div>
            {showUploaded && (
              <div className="border border-gray-300 rounded p-4 bg-white space-y-4 mt-2" data-testid="docs-uploaded-list">
                {uploaded.length > 0 ? uploaded.map(doc => (
                  <div key={doc.field} className="flex items-start space-x-3">
                    <Checkbox id={`uploaded-${doc.field}`} checked={true} onCheckedChange={() => handleToggle(doc.field)} className="w-5 h-5 border-2 mt-0.5 data-[state=checked]:bg-blue-600" data-testid={`uploaded-check-${doc.field}`} />
                    <label htmlFor={`uploaded-${doc.field}`} className="text-sm text-gray-700 leading-snug cursor-pointer">{doc.label}</label>
                  </div>
                )) : (
                  <p className="text-sm text-gray-400 text-center">No documents uploaded yet</p>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Save warning dialog */}
        {showSaveWarning && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="docs-save-warning">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-4">
              <p className="text-gray-800 font-semibold text-base mb-4">{saveWarningMsg}</p>
              <div className="flex items-center justify-end gap-3">
                <button onClick={doSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700" data-testid="docs-warn-confirm">
                  {saving ? 'Saving...' : 'Confirm'}
                </button>
                <button onClick={() => setShowSaveWarning(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300" data-testid="docs-warn-goback">
                  Go Back
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocsModal;
